import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";

import ChatPanel from "./ChatPanel";
import CoachBubble from "./CoachBubble";
import JitBubble from "./JitBubble";
import "./style.css";
import { resolveTourConfig, DEFAULT_TOUR_ROUTE } from "./tourConfigs";
import { cachedFeatureOn } from "../officeFeatures";
import {
  selectTourActive, selectTourMode, selectStepIndex, selectJitStepId, selectDemoActive, selectTourName,
  startTour, nextStep, prevStep, exitTour, completeTour, setDemo, showJitStep,
} from "../../reducers/guidedOnboarding";
import {
  getTourState, postTourStart, postTourStep, postTourComplete, postTourSkip, postTourExit, postTourRemind,
} from "./onboardingController";

const REMIND_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const IDLE_MS = 5000; // sit still this long → surface one outstanding coach-mark
const EMPTY_STEPS = [];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const sel = (s) => document.querySelector(`[data-onboarding-id="${s}"]`);
const isVisible = (el) => !!(el && el.offsetParent !== null);

async function resolve(selector, timeout = 3500) {
  if (!selector) return null;
  const deadline = Date.now() + timeout;
  while (Date.now() < deadline) {
    const el = sel(selector);
    if (el && el.offsetParent !== null) return el;
    await sleep(60);
  }
  return null;
}

// Fire a full pointer sequence so antd controls that listen on mousedown (e.g.
// Select options) actually register the interaction — a bare .click() is ignored.
function fireClick(el) {
  if (!el) return;
  ["mousedown", "mouseup", "click"].forEach((type) =>
    el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }))
  );
}

async function runPreActions(preActions) {
  for (const pa of preActions || []) {
    try {
      if (pa.type === "wait") await sleep(pa.ms || 200);
      else if (pa.type === "click") { const el = sel(pa.selector); if (el) el.click(); }
      else if (pa.type === "openSelect") {
        // Open a controlled antd Select: click its real trigger (.ant-select-selector)
        // inside the marked host — clicking the host wrapper does NOT open it.
        const host = sel(pa.selector);
        fireClick(host && (host.querySelector(".ant-select-selector") || host));
      }
      else if (pa.type === "clickFirstOption") {
        fireClick(document.querySelector(".ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option"));
      }
    } catch (e) { /* best-effort */ }
    await sleep(120);
  }
}

const GuidedOnboardingLayer = (props) => {
  const { mode, stepIndex, jitStepId, location, activeTourName } = props;
  const [rect, setRect] = useState(null);
  const [completed, setCompleted] = useState(true); // assume done until we know
  const [seen, setSeen] = useState([]);
  const [showReminder, setShowReminder] = useState(false);
  const runToken = useRef(0);
  const bootstrappedTours = useRef({}); // tourName -> true once bootstrapped this session

  // The app navigates via a hash history (store/configureStore createHashHistory),
  // so normal navigation lands on /#/treatment-plans — where react-router's
  // pathname is "/" and the tour would never bootstrap. Re-evaluate the resolved
  // tour config on hash/popstate changes (the outer BrowserRouter location does
  // not update on hash-only changes).
  const [, bumpRoute] = useState(0);
  useEffect(() => {
    const onUrlChange = () => bumpRoute((n) => n + 1);
    window.addEventListener("hashchange", onUrlChange);
    window.addEventListener("popstate", onUrlChange);
    return () => {
      window.removeEventListener("hashchange", onUrlChange);
      window.removeEventListener("popstate", onUrlChange);
    };
  }, []);

  const hashPath = (window.location.hash || "").replace(/^#/, "").split("?")[0];
  const config = resolveTourConfig(location, hashPath); // stable ref per tour (or null)
  const onRoute = !!config;
  const tourName = config ? config.tourName : null;
  const STEPS = config ? config.steps : EMPTY_STEPS;
  const TOTAL_STEPS = STEPS.length;

  // Latest config for handlers whose effect runs once (mount) — avoids stale closures.
  const configRef = useRef(config);
  configRef.current = config;

  // Only act for the tour that matches BOTH the current route and the store's
  // active-tour name, so a super-admin hopping /treatment-plans <-> /ftc-tasks
  // never renders one tour's state on the other page.
  const active = props.active && !!config && activeTourName === tourName;
  const getStepById = (id) => STEPS.find((s) => s.id === id) || null;
  const getStepBySelector = (s) => STEPS.find((x) => x.selector === s) || null;
  const step = active ? (mode === "jit" ? getStepById(jitStepId) : (STEPS[stepIndex] || null)) : null;

  const isModalOpen = () => !!(config && config.modalId && isVisible(sel(config.modalId)));

  // Mirror demoActive -> localStorage + a window event so the (unconnected) patient
  // board / FTC queue can include the demo row in its fetch and refresh.
  useEffect(() => {
    if (props.demoActive) localStorage.setItem("tourDemoActive", "1");
    else localStorage.removeItem("tourDemoActive");
    window.dispatchEvent(new Event("guided-onboarding-demo"));
  }, [props.demoActive]);

  // Begin a fresh tour run (auto-start, the "Take the tour" icon, resume). Resets
  // the backend run so completed/skipped/exited reflect THIS run.
  const beginRun = useCallback(async () => {
    const cfg = configRef.current;
    if (!cfg) return;
    const officeId = localStorage.getItem("selectedOffice");
    if (cfg.ensureDemo) await cfg.ensureDemo(officeId);
    await postTourStart(cfg.tourName);
    setSeen([]);
    setCompleted(false);
    setShowReminder(false);
    props.startTour(0, cfg.tourName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startTour]);

  // ---- bootstrap: fetch state, auto-start or reminder (once per tour/session) ----
  useEffect(() => {
    if (!config || bootstrappedTours.current[tourName]) return;
    bootstrappedTours.current[tourName] = true;
    (async () => {
      // We navigated here from the header "Take the tour" icon on another page →
      // start a fresh run regardless of prior state.
      let pending = false;
      try { pending = sessionStorage.getItem("go-tour-start-pending") === "1"; } catch (e) {}
      if (pending) {
        try { sessionStorage.removeItem("go-tour-start-pending"); } catch (e) {}
        await beginRun();
        return;
      }
      const state = await getTourState(tourName);
      if (!state) return;
      setCompleted(!!state.completed);
      setSeen(state.seen_steps || []);
      if (!state.completed) {
        const officeId = localStorage.getItem("selectedOffice");
        if (config.ensureDemo) await config.ensureDemo(officeId);
        // Keep the demo row present while the tour is incomplete — the row-level
        // coach-marks are instrumented only on that row. Cleared on complete/skip.
        props.setDemo(true);
        if (!state.exited) {
          props.startTour(0, tourName); // first run: auto-start
        } else {
          const last = state.last_reminded_at ? Date.parse(state.last_reminded_at) : 0;
          if (Date.now() - last > REMIND_COOLDOWN_MS) setShowReminder(true);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourName]);

  // ---- manual replay ("Take the tour" in the TopBar fires a window event) ----
  useEffect(() => {
    const onStart = async () => {
      const cfg = configRef.current;
      if (cfg) {
        // On a tour route → start that tour. If the board element isn't present
        // yet, navigate to its route + leave a flag; the bootstrap starts the run.
        const onBoard = !!sel(cfg.boardId);
        if (!onBoard) {
          try { sessionStorage.setItem("go-tour-start-pending", "1"); } catch (e) {}
          props.push(cfg.route);
          return;
        }
        bootstrappedTours.current[cfg.tourName] = true;
        await beginRun();
        return;
      }
      // Off any tour route → default to the clinic tour — unless Treatment
      // Planning (where that tour lives) is switched off for this office.
      if (!cachedFeatureOn("treatment_planning")) return;
      try { sessionStorage.setItem("go-tour-start-pending", "1"); } catch (e) {}
      props.push(DEFAULT_TOUR_ROUTE);
    };
    window.addEventListener("guided-onboarding-start", onStart);
    return () => window.removeEventListener("guided-onboarding-start", onStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lowest-index outstanding JIT step whose target is in (and visible in) the
  // current view — the modal if it's open, otherwise the board. STEPS is in
  // manifest order, so the first match is the lowest index. (No-op for tours with
  // no jit steps, e.g. the FTC tour v1.)
  const pickIdleStep = useCallback(() => {
    if (!config) return null;
    const inModal = isModalOpen();
    for (const s of STEPS) {
      if (!s.jit || seen.indexOf(s.id) !== -1) continue;
      if (!!s.insideModal !== inModal) continue;
      if (isVisible(sel(s.selector))) return s;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen, config]);

  // ---- mark a tip "learned" when the user genuinely USES its control ----
  useEffect(() => {
    const onClick = (e) => {
      if (completed || active || !config) return;
      let node = e.target;
      while (node && node !== document.body) {
        const id = node.getAttribute && node.getAttribute("data-onboarding-id");
        if (id) {
          const s = getStepBySelector(id);
          if (s && s.jit && seen.indexOf(s.id) === -1) {
            setSeen((prev) => (prev.indexOf(s.id) === -1 ? [...prev, s.id] : prev));
            postTourStep(s.id, undefined, tourName);
          }
          return;
        }
        node = node.parentNode;
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, active, seen, tourName]);

  // ---- JIT idle nudge: after a stretch of inactivity, gently surface ONE
  // outstanding coach-mark (bubble only — no side panel, no screen dim) ----
  useEffect(() => {
    if (!onRoute || completed || active) return undefined; // one at a time, never over the linear tour
    let timer = null;
    const fire = () => {
      if (completed || active) return;
      const s = pickIdleStep();
      if (!s) return;
      setSeen((prev) => (prev.indexOf(s.id) === -1 ? [...prev, s.id] : prev));
      postTourStep(s.id, undefined, tourName);
      props.showJitStep(s.id);
    };
    const arm = () => { if (timer) clearTimeout(timer); timer = setTimeout(fire, IDLE_MS); };
    // Reset only on genuine INTERACTION (click / key / tap). Passive looking-around
    // — moving the mouse, scrolling, reading — keeps the timer running.
    const events = ["mousedown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, arm));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRoute, completed, active, seen, pickIdleStep, tourName]);

  // ---- run the current step: route-guard -> preActions -> resolve -> anchor ----
  useEffect(() => {
    if (!step) { setRect(null); return; }
    const token = ++runToken.current;
    (async () => {
      if (!onRoute && config && props.history) props.history.push(config.route);
      // Idle JIT nudges must NOT fire preActions (they open modals / switch tabs);
      // the target is already on-screen, so just anchor to it.
      if (mode === "linear") await runPreActions(step.preActions);
      if (token !== runToken.current) return;
      if (!step.selector) { setRect(null); }
      else {
        const el = await resolve(step.selector);
        if (token !== runToken.current) return;
        if (!el) {
          // eslint-disable-next-line no-console
          console.warn("[tour] unresolved step target", step.id, step.selector);
          if (mode === "linear") { advance(); return; }
          else { props.exitTour(); return; }
        }
        el.scrollIntoView({ block: mode === "jit" ? "nearest" : "center", behavior: "auto" });
        await new Promise((r) => requestAnimationFrame(r));
        await sleep(60);
        if (token !== runToken.current) return;
        setRect(el.getBoundingClientRect());
      }
      // record the step as seen
      if (seen.indexOf(step.id) === -1) {
        setSeen((p) => [...p, step.id]);
        postTourStep(step.id, mode === "linear" ? stepIndex : undefined, tourName);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step && step.id]);

  // ---- keep the spotlight locked on the element through ANY layout shift ----
  useEffect(() => {
    if (!step || !step.selector) return undefined;
    let raf;
    let last = "";
    const tick = () => {
      const el = sel(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        const key = r.top + "," + r.left + "," + r.width + "," + r.height;
        if (key !== last) { last = key; setRect(r); }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step && step.id]);

  const advance = useCallback(() => {
    if (mode === "jit") { props.exitTour(); return; }
    if (stepIndex >= TOTAL_STEPS - 1) {
      postTourComplete(tourName);
      props.completeTour(); // clears demoActive
    } else {
      props.nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, stepIndex, TOTAL_STEPS, tourName, props]);

  // Exit ≠ done: leave the demo row in place so the idle coach-marks can keep
  // teaching. The demo is cleared only when the tour completes or is skipped.
  const onExit = () => { postTourExit(mode === "linear" ? stepIndex : undefined, tourName); props.exitTour(); };
  const onSkip = () => { postTourSkip(tourName); props.completeTour(); setCompleted(true); };

  const dismissReminder = () => { setShowReminder(false); postTourRemind(tourName); };
  const resumeFromReminder = async () => {
    setShowReminder(false);
    const officeId = localStorage.getItem("selectedOffice");
    if (config && config.ensureDemo) await config.ensureDemo(officeId);
    await postTourStart(tourName);   // fresh run: clear prior completed/skipped/exited
    setSeen([]);
    setCompleted(false);
    props.startTour(0, tourName);
  };

  // ---- side-by-side layout while the panel is open (linear only) ----
  const panelOpen = !!(active && step && mode === "linear");
  useEffect(() => {
    if (!panelOpen) return undefined;
    document.body.classList.add("go-tour-open");
    window.dispatchEvent(new CustomEvent("go-sidebar", { detail: { collapsed: true } }));
    return () => {
      document.body.classList.remove("go-tour-open");
      window.dispatchEvent(new CustomEvent("go-sidebar", { detail: { restore: true } }));
      // Dismiss a lingering tag-note popover if the tour ends while it's open.
      const cancel = document.querySelector('[data-onboarding-id="tag-note-cancel"]');
      if (cancel) cancel.click();
    };
  }, [panelOpen]);

  // ---- render ----
  return ReactDOM.createPortal(
    <>
      {active && step ? (
        mode === "jit" ? (
          rect ? (
            <JitBubble rect={rect} step={step} onGotIt={() => props.exitTour()} onStop={onSkip} />
          ) : null
        ) : (
          <>
            {!step.selector ? <div className="go-scrim" aria-hidden="true" /> : null}
            {rect ? <CoachBubble rect={rect} title={step.title} side={step.side} insideModal={step.insideModal} /> : null}
            <ChatPanel
              step={step}
              index={mode === "linear" ? stepIndex : 0}
              total={TOTAL_STEPS}
              isJit={mode === "jit"}
              canBack={mode === "linear" && stepIndex > 0}
              isLast={mode === "linear" && stepIndex >= TOTAL_STEPS - 1}
              hostName={config ? config.hostName : undefined}
              onNext={advance}
              onBack={() => props.prevStep()}
              onSkip={onSkip}
              onExit={onExit}
            />
          </>
        )
      ) : null}

      {!active && showReminder && config ? (
        <div className="go-reminder" role="status">
          <span>👋 Finish the quick {config.reminderLabel} tour?</span>
          <div>
            <button className="go-btn go-btn-link" onClick={dismissReminder}>Not now</button>
            <button className="go-btn go-btn-primary" onClick={resumeFromReminder}>Resume</button>
          </div>
        </div>
      ) : null}
    </>,
    document.body
  );
};

const mapState = (state) => ({
  active: selectTourActive(state),
  mode: selectTourMode(state),
  stepIndex: selectStepIndex(state),
  jitStepId: selectJitStepId(state),
  demoActive: selectDemoActive(state),
  activeTourName: selectTourName(state),
});
const mapDispatch = { startTour, nextStep, prevStep, exitTour, completeTour, setDemo, showJitStep, push };

export default withRouter(connect(mapState, mapDispatch)(GuidedOnboardingLayer));
